import { useState, useCallback } from "react";
import { message } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { authAxios } from "../../utils/session_utils";

const API_BASE = process.env.REACT_APP_BACK_END_API || "";
const CONCURRENT_UPLOADS = 6; // Number of parallel uploads
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

interface UploadPart {
	partNumber: number;
	chunk: Blob;
	signedUrl: string;
}

interface UploadProgress {
	uploaded: number;
	total: number;
	percentage: number;
	status: "idle" | "uploading" | "completing" | "success" | "error";
	error?: string;
}

interface InitUploadResponse {
	uploadId: string;
	key: string;
	signedUrls: string[];
	totalParts: number;
	partSize: number;
}

interface CompleteUploadResponse {
	success: boolean;
	location: string;
	key: string;
	etag: string;
}

const useVideoComplianceUploadHook = () => {
	const [loading, setLoading] = useState<boolean>(false);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [file, setFile] = useState<File | null>(null);
	const [progress, setProgress] = useState<UploadProgress>({
		uploaded: 0,
		total: 0,
		percentage: 0,
		status: "idle",
	});

	const handleFileRemove = useCallback(() => {
		setFile(null);
		setFileList([]);
		setProgress({
			uploaded: 0,
			total: 0,
			percentage: 0,
			status: "idle",
		});
		return true;
	}, []);

	const beforeUpload = useCallback((file: File) => {
		// Accept all video files - check by MIME type or extension
		const isVideoFile = 
			file.type.startsWith("video/") ||
			/\.(mp4|mov|avi|mkv|wmv|flv|webm|m4v|3gp|mpg|mpeg|ts|mts)$/i.test(file.name);
		
		if (!isVideoFile) {
			message.error("Please select a valid video file");
			return false;
		}

		setFileList([file as any]);
		setFile(file);
		setProgress({
			uploaded: 0,
			total: file.size,
			percentage: 0,
			status: "idle",
		});
		return false; // Prevent auto upload
	}, []);

	/**
	 * Upload a single part with retry logic
	 */
	const uploadPartWithRetry = async (
		part: UploadPart,
		retries: number = MAX_RETRIES
	): Promise<{ PartNumber: number; ETag: string }> => {
		for (let attempt = 1; attempt <= retries; attempt++) {
			try {
				const response = await fetch(part.signedUrl, {
					method: "PUT",
					body: part.chunk,
					headers: {
						"Content-Type": part.chunk.type || "application/octet-stream",
					},
				});

				if (!response.ok) {
					throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
				}

				const etag = response.headers.get("ETag");
				if (!etag) {
					throw new Error("No ETag received from S3");
				}

				// Remove quotes from ETag if present
				const cleanEtag = etag.replace(/"/g, "");

				return {
					PartNumber: part.partNumber,
					ETag: cleanEtag,
				};
			} catch (error: any) {
				if (attempt === retries) {
					throw new Error(`Failed to upload part ${part.partNumber} after ${retries} attempts: ${error.message}`);
				}
				// Wait before retrying
				await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * attempt));
			}
		}
		throw new Error("Upload failed after all retries");
	};

	/**
	 * Upload parts in parallel with concurrency limit
	 */
	const uploadPartsInParallel = async (
		parts: UploadPart[],
		onProgress: (uploaded: number) => void
	): Promise<Array<{ PartNumber: number; ETag: string }>> => {
		const results: Array<{ PartNumber: number; ETag: string }> = [];
		let uploadedBytes = 0;

		// Process parts in batches
		for (let i = 0; i < parts.length; i += CONCURRENT_UPLOADS) {
			const batch = parts.slice(i, i + CONCURRENT_UPLOADS);

			const batchPromises = batch.map(async (part) => {
				const result = await uploadPartWithRetry(part);
				uploadedBytes += part.chunk.size;
				onProgress(uploadedBytes);
				return result;
			});

			const batchResults = await Promise.all(batchPromises);
			results.push(...batchResults);
		}

		return results;
	};

	/**
	 * Slice file into chunks based on part size
	 */
	const sliceFileIntoChunks = (file: File, partSize: number): Blob[] => {
		const chunks: Blob[] = [];
		let start = 0;

		while (start < file.size) {
			const end = Math.min(start + partSize, file.size);
			chunks.push(file.slice(start, end));
			start = end;
		}

		return chunks;
	};

	/**
	 * Initialize multipart upload
	 */
	const initUpload = async (file: File): Promise<InitUploadResponse> => {
		const config = {
			method: "POST",
			url: `${API_BASE}/video-compliance/upload/init`,
			headers: {
				"Content-Type": "application/json",
			},
			data: {
				fileName: file.name,
				fileSize: file.size,
				contentType: file.type || "video/mp4",
			},
		};

		const response = await authAxios(config);
		return response.data;
	};

	/**
	 * Complete multipart upload
	 */
	const completeUpload = async (
		uploadId: string,
		key: string,
		parts: Array<{ PartNumber: number; ETag: string }>
	): Promise<CompleteUploadResponse> => {
		const config = {
			method: "POST",
			url: `${API_BASE}/video-compliance/upload/complete`,
			headers: {
				"Content-Type": "application/json",
			},
			data: {
				uploadId,
				key,
				parts,
			},
		};

		const response = await authAxios(config);
		return response.data;
	};

	/**
	 * Main upload handler
	 */
	const handleUpload = useCallback(async () => {
		if (!file) {
			message.error("Please select a file first!");
			return;
		}

		setLoading(true);
		setProgress((prev) => ({ ...prev, status: "uploading" }));

		try {
			// Step 1: Initialize multipart upload
			message.loading("Initializing upload...", 0);
			const initResponse = await initUpload(file);
			message.destroy();

			const { uploadId, key, signedUrls, partSize } = initResponse;

			// Step 2: Slice file into chunks
			const chunks = sliceFileIntoChunks(file, partSize);

			if (chunks.length !== signedUrls.length) {
				throw new Error(`Mismatch: ${chunks.length} chunks but ${signedUrls.length} signed URLs`);
			}

			// Step 3: Create upload parts
			const uploadParts: UploadPart[] = chunks.map((chunk, index) => ({
				partNumber: index + 1,
				chunk,
				signedUrl: signedUrls[index],
			}));

			// Step 4: Upload parts in parallel
			setProgress((prev) => ({
				...prev,
				status: "uploading",
			}));

			const uploadedParts = await uploadPartsInParallel(uploadParts, (uploaded) => {
				setProgress({
					uploaded,
					total: file.size,
					percentage: Math.round((uploaded / file.size) * 100),
					status: "uploading",
				});
			});

			// Step 5: Complete multipart upload
			setProgress((prev) => ({
				...prev,
				status: "completing",
			}));

			const completeResponse = await completeUpload(uploadId, key, uploadedParts);

			// Success!
			setProgress({
				uploaded: file.size,
				total: file.size,
				percentage: 100,
				status: "success",
			});

			message.success(`Video uploaded successfully! Location: ${completeResponse.location}`);
			setFile(null);
			setFileList([]);

			// Reset progress after 3 seconds
			setTimeout(() => {
				setProgress({
					uploaded: 0,
					total: 0,
					percentage: 0,
					status: "idle",
				});
			}, 3000);
		} catch (error: any) {
			console.error("Upload error:", error);
			setProgress({
				uploaded: 0,
				total: file.size,
				percentage: 0,
				status: "error",
				error: error.message || "Upload failed",
			});
			message.error(`Upload failed: ${error.message || "Unknown error"}`);
		} finally {
			setLoading(false);
			message.destroy();
		}
	}, [file]);

	return {
		loading,
		file,
		fileList,
		progress,
		handleUpload,
		handleFileRemove,
		beforeUpload,
	};
};

export default useVideoComplianceUploadHook;
